import App from "@bloglovin/inspire-engine/App";
import * as PrimitiveContextValue from "@bloglovin/inspire-engine/Helpers/PrimitiveContextValue";
import { bootInstalledDbApps } from "%/helpers/boot-installed-db-apps";
import { Context } from "%/dynamic-component-editor/classes/context";
import ProvidesContextsResolver from "%/dynamic-component-editor/classes/provides-contexts-resolver";
import StandardContextEntities from "%/dynamic-component-editor/classes/standard-context-entities";
import ProvidesContextEntry from "%/dynamic-component-editor/classes/provides-context-entry";
import ContextRegistryScope from "%/dynamic-component-editor/classes/context-registry-scope";
import { getHostEntityTypes, getLegacyContextsEntities } from "@studio/Helpers/studio-legacy-entities-defs-bootstrap";
import { readRequiredContextsFromRootOptions } from "%/dynamic-component-editor/helpers/story-card-candidates";
import { resolveContextEngineAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { readUnsetRefAsNull } from "%/dynamic-component-editor/helpers/unset-ref-read";

// eslint-disable-next-line global-require
const useReadStatusStore = (...args) => require("%/dynamic-component-editor/stores/read-status-store").useReadStatusStore(...args);


export const TEMP_PK_THRESHOLD = 1e10;

export function getDbAppsCatalog() {
  return window.blclient?.data?.dbApps ?? {};
}

export function computeInstalledDbAppNames(rootAppName, destinationId) {
  const catalog = getDbAppsCatalog();
  const app = App.get(rootAppName);
  const destinationPk = { id: String(destinationId) };
  const items = app.getFeedItems("Destination", destinationPk, "appInstalls") ?? [];
  const names = [];
  items.forEach(item => {
    if (!item.entityPk || Number(item.entityPk.id) >= TEMP_PK_THRESHOLD) {
      return;
    }
    const name = app.getEntityField("DestinationApp", item.entityPk, "appName");
    if (name && catalog[name] && !names.includes(name)) {
      names.push(name);
    }
  });
  return names.sort();
}

export function buildDbAppContextSource(appName, catalogEntry) {
  const config = catalogEntry?.config ?? {};
  const entities = {};
  Object.entries(config.entities ?? {}).forEach(([entityType, entityConfig]) => {
    entities[entityType] = { ...entityConfig, appName };
  });
  return {
    appName,
    displayName: catalogEntry?.title || appName,
    title: catalogEntry?.title || appName,
    entities,
    events: config.events ?? {},
    components: config.components ?? {},
    dependencies: config.dependencies ?? [],
    appUser: config.appUser ?? null,
    configurationEntity: config.configurationEntity ?? "Configuration",
  };
}

export function computeInstalledDbAppTenants(rootAppName, destinationId) {
  const catalog = getDbAppsCatalog();
  const app = App.get(rootAppName);
  const destinationPk = { id: String(destinationId) };
  const items = app.getFeedItems("Destination", destinationPk, "appInstalls") ?? [];
  const tenants = {};
  items.forEach(item => {
    if (!item.entityPk || Number(item.entityPk.id) >= TEMP_PK_THRESHOLD) {
      return;
    }
    const name = app.getEntityField("DestinationApp", item.entityPk, "appName");
    const configurationId = Number(app.getEntityField("DestinationApp", item.entityPk, "configurationId")) || 0;
    if (name && catalog[name] && configurationId > 0) {
      tenants[name] = configurationId;
    }
  });
  return tenants;
}

export function pinInstalledAppSessions(tenants) {
  const catalog = getDbAppsCatalog();
  Object.keys(catalog).forEach(appName => {
    let engineApp;
    try {
      engineApp = App.get(appName);
    } catch (error) {
      return; // not booted (yet) — the boot path pins via resolveInstalledAppContexts
    }
    const configurationId = tenants[appName] ?? 0;
    engineApp.setSessionConfigurationPk(configurationId > 0 ? { id: configurationId } : null);
  });
}

export function syncInstalledAppContextSources(installedNames) {
  const catalog = getDbAppsCatalog();
  const installed = new Set(installedNames);
  Object.keys(catalog).forEach(name => {
    if (installed.has(name)) {
      Context.addSource(name, buildDbAppContextSource(name, catalog[name]));
    } else {
      Context.removeSource(name);
    }
  });
}

// The stable context NAME an auto-present standard-context row registers under.
// currentUser is ambient (no app); appConfiguration / appUser are per-app so
// they namespace by app name to stay collision-free across installs.
// XXX: EVERY standard row must name itself. The trailing arm used to be a
// XXX: catch-all `return "currentUser"`, which silently named the
// XXX: currentUserProfile row "currentUser" too -- and since these names are the
// XXX: KEYS of the entries map, the UserProfile-typed row then OVERWROTE the real
// XXX: currentUser entry. The surviving entry was rejected as not-owned, so the
// XXX: standard currentUser context was seeded with NO pk at all and every
// XXX: element bound to it read empty. A catch-all that invents an identity for
// XXX: a row it does not recognise cannot fail visibly; an unknown row must
// XXX: throw and name itself. See task #116.
export function standardContextName(row) {
  if (row.standardEntity === "appConfiguration") {
    return `${row.appName}Configuration`;
  }
  if (row.standardEntity === "appUser") {
    return `${row.appName}User`;
  }
  if (row.standardEntity === ProvidesContextEntry.KEY_STANDARD_RESUME_STORY) {
    return ProvidesContextEntry.KEY_STANDARD_RESUME_STORY;
  }
  if (row.standardEntity === ProvidesContextEntry.KEY_STANDARD_CURRENT_USER_PROFILE) {
    return ProvidesContextEntry.KEY_STANDARD_CURRENT_USER_PROFILE;
  }
  if (row.standardEntity === ProvidesContextEntry.KEY_STANDARD_CURRENT_SUBSCRIPTION) {
    return ProvidesContextEntry.KEY_STANDARD_CURRENT_SUBSCRIPTION;
  }
  if (row.standardEntity === ProvidesContextEntry.KEY_STANDARD_CURRENT_USER_ACCESS) {
    return ProvidesContextEntry.KEY_STANDARD_CURRENT_USER_ACCESS;
  }
  if (!row.standardEntity || row.standardEntity === "currentUser") {
    return "currentUser";
  }
  throw new Error(
    `standardContextName: unknown standard row "${row.standardEntity}" (entityType `
    + `"${row.entityType}"). Every standard context must name itself here — add the arm, or the row `
    + "will collide with another context's name and silently replace it.",
  );
}

export function standardContextRows(installedNames) {
  const installed = new Set(installedNames);
  const appSources = Object.fromEntries(
    Object.entries(Context.allSources ?? {})
        .filter(([name, source]) => source?.appName && installed.has(name)),
  );
  return StandardContextEntities.enumerate({ sources: appSources, catalog: getDbAppsCatalog() });
}

function standardContextEntry(row) {
  const entry = {
    entityType: row.entityType,
    entitySource: "standard",
    standardEntity: row.standardEntity ?? "currentUser",
  };
  if (row.appName) {
    entry.appName = row.appName;
  }
  return entry;
}

export function standardContextProvidesEntries(installedNames) {
  const entries = {};
  standardContextRows(installedNames).forEach(row => {
    if (row.standardEntity === ProvidesContextEntry.KEY_STANDARD_RESUME_STORY) {
      return;
    }
    entries[standardContextName(row)] = standardContextEntry(row);
  });
  return entries;
}

export function registerStandardContexts(installedNames) {
  const placed = [];
  standardContextRows(installedNames).forEach(row => {
    const name = standardContextName(row);
    if (Context.getContext(name)) {
      return;
    }
    Context.addContext(name, {
      ...standardContextEntry(row),
      entityPkUrlVars: {},
      registryScope: ContextRegistryScope.KEY_AMBIENT,
    });
    placed.push(name);
  });
  return placed;
}

export function dropUnresolvedStandardContexts(placed, resolved, removeContext) {
  placed.filter(name => !resolved?.[name]).forEach(name => removeContext(name));
}

export function getInstalledAppComponents(rootAppName, destinationId) {
  const catalog = getDbAppsCatalog();
  return computeInstalledDbAppNames(rootAppName, destinationId)
      .map(appName => {
        const source = buildDbAppContextSource(appName, catalog[appName]);
        const components = Object.entries(source.components)
            .map(([componentName, definition]) => ({ componentName, definition }))
            .sort((first, second) => first.componentName.localeCompare(second.componentName));
        return { appName, displayName: source.displayName, components };
      })
      .filter(appEntry => appEntry.components.length > 0);
}

// The open destination's own app (task 135): pickable before it's deployed, never booted. See DestinationAppDraft.
let destinationAppDraft = null;

function draftContextSource(draft) {
  return buildDbAppContextSource(draft.appName, {
    title: draft.title,
    config: { entities: draft.entities, configurationEntity: draft.configurationEntity },
  });
}

export function setDestinationAppDraft(draft) {
  if (destinationAppDraft && destinationAppDraft.appName !== draft?.appName) {
    Context.removeSource(destinationAppDraft.appName);
  }
  destinationAppDraft = draft?.appName ? draft : null;
  if (destinationAppDraft) {
    Context.addSource(destinationAppDraft.appName, draftContextSource(destinationAppDraft));
  }
}

export function getInstalledContextsEntities(installedNames) {
  const catalog = getDbAppsCatalog();
  const entities = { ...getLegacyContextsEntities() };
  installedNames.forEach(appName => {
    const source = buildDbAppContextSource(appName, catalog[appName]);
    Object.assign(entities, source.entities);
  });
  if (destinationAppDraft) {
    Object.assign(entities, draftContextSource(destinationAppDraft).entities);
  }
  return entities;
}


export function describeDynamicComponent(dynamicComponent) {
  const rootOptions = dynamicComponent?.rootOptions ?? {};
  return rootOptions["display-name"] || rootOptions["page-title"] || rootOptions["component-name"]
    || `DC ${dynamicComponent?.id ?? "?"}`;
}

export function readProvidesContexts(dynamicComponent) {
  const providesContexts = (dynamicComponent.rootOptions ?? {})["provides-contexts"];
  return providesContexts && typeof providesContexts === "object" ? providesContexts : {};
}

export function readRequiredContexts(dynamicComponent) {
  return readRequiredContextsFromRootOptions(dynamicComponent?.rootOptions);
}

export function resolveMaterializedAppComponentId(rootAppName, appName, componentName, destinationId) {
  if (!appName || !componentName || !destinationId) {
    return null;
  }
  let app;
  try {
    app = App.get(rootAppName);
  } catch (error) {
    return null; // root not booted yet — caller re-renders reactively
  }
  const destinationPk = { id: String(destinationId) };
  const meta = app.getFeedMeta("Destination", destinationPk, "components", "hasLoadedAtLeastOnce");
  if (!meta) {
    app.triggerFeedInitialLoad("Destination", destinationPk, "components");
  }
  const items = app.getFeedItems("Destination", destinationPk, "components") ?? [];
  for (const item of items) {
    if (!item.entityPk || Number(item.entityPk.id) >= TEMP_PK_THRESHOLD) {
      continue;
    }
    if (app.getEntityField("DynamicComponent", item.entityPk, "appName") !== appName) {
      continue;
    }
    if (app.getEntityField("DynamicComponent", item.entityPk, "status") === "inactive") {
      continue;
    }
    const rootOptions = app.getEntityField("DynamicComponent", item.entityPk, "rootOptions") ?? {};
    const parsed = typeof rootOptions === "string" ? safeParseJson(rootOptions) : rootOptions;
    if ((parsed?.["app-source-component"] ?? "") === componentName) {
      return Number(item.entityPk.id);
    }
  }
  return null;
}

function safeParseJson(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

export function overlayResolvedPageEntities(pageEntities, dynamicComponent) {
  const overlaid = { ...pageEntities ?? {} };
  for (const name of Object.keys(readProvidesContexts(dynamicComponent))) {
    const id = Context.getContext(name)?.entityPkUrlVars?.id;
    if (id !== undefined && id !== null && id !== "") {
      overlaid[name] = id;
    }
  }
  return overlaid;
}

function bootedAppCurrentUserPk(appName) {
  try {
    const enginePk = App.get(appName).getCurrentUserEntityPk?.();
    return enginePk && Object.keys(enginePk).length ? enginePk : null;
  } catch {
    return null;
  }
}

export function createEngineContextBridge() {
  return {
    getCurrentUserEntityPk(appName) {
      const enginePk = appName ? bootedAppCurrentUserPk(appName) : null;
      if (enginePk) {
        return enginePk;
      }
      const userId = Number(window.blclient?.data?.inspirationUserId) || 0;
      return userId ? { id: userId } : null;
    },
    isViewerSignedIn() {
      return (Number(window.blclient?.data?.inspirationUserId) || 0) > 0;
    },
    // renderDestinationSlug seeds the row under the destination's own user id.
    getCurrentUserAccessPk(appName) {
      const seededId = Number(window.blclient?.data?.viewerAccess?.id) || 0;
      return seededId ? { id: seededId } : this.getCurrentUserEntityPk(appName);
    },
    getCurrentSubscriptionPk() {
      const subscriptionId = Number(window.blclient?.data?.subscriberData?.subscription_id) || 0;
      return subscriptionId ? { id: subscriptionId } : null;
    },
    getResumeStoryPk() {
      const readStatusStore = useReadStatusStore();
      return readStatusStore.loadResumeStory().then(() => {
        return readStatusStore.resumeStoryId ? { id: readStatusStore.resumeStoryId } : null;
      });
    },
    readOneEntityPk,
    readConfigurationOneEntityPk,
  };
}

export async function resolveInstalledAppContexts(dynamicComponent, urlVars) {
  const catalog = getDbAppsCatalog();
  syncInstalledAppContextSources(Object.keys(catalog));

  await bootInstalledDbApps();

  await Promise.all(Object.keys(catalog).map(appName => waitForAppBoot(appName)));

  Object.entries(catalog).forEach(([appName, catalogEntry]) => {
    const configurationId = Number(catalogEntry?.configurationId) || 0;
    if (configurationId) {
      App.get(appName).setSessionConfigurationPk({ id: configurationId });
    }
  });

  const placedStandardContexts = registerStandardContexts(Object.keys(catalog));

  const resolvedStandardContexts = await ProvidesContextsResolver.resolveAll({
    providesContexts: standardContextProvidesEntries(Object.keys(catalog)),
    urlVars,
    catalog,
    allowUri: true,
    hostEntityTypes: getHostEntityTypes(),
    lookupContext: name => Context.getContext(name) ?? null,
    registerContext: (name, context) => Context.addContext(name, context),
    primitiveContextValue: PrimitiveContextValue,
    registerValueContext: (name, value) => Context.addValueContext(name, value),
    bridge: createEngineContextBridge(),
    measureLabel: "standard-context",
    surfaceLabel: describeDynamicComponent(dynamicComponent),
  });
  dropUnresolvedStandardContexts(placedStandardContexts, resolvedStandardContexts, name => Context.removeContext(name));

  await ProvidesContextsResolver.resolveAll({
    providesContexts: readProvidesContexts(dynamicComponent),
    urlVars,
    catalog,
    allowUri: true,
    hostEntityTypes: getHostEntityTypes(),
    hardFailOnUnresolved: true,
    lookupContext: name => Context.getContext(name) ?? null,
    registerContext: (name, context) => Context.addContext(name, context),
    primitiveContextValue: PrimitiveContextValue,
    registerValueContext: (name, value) => Context.addValueContext(name, value),
    bridge: createEngineContextBridge(),
    measureLabel: "page",
    surfaceLabel: describeDynamicComponent(dynamicComponent),
  });
}

export function readOneEntityPk(appName, entityType, entityPk, fieldName, timeoutMs = 15000) {
  const hasPk = entityPk && typeof entityPk === "object"
    ? Object.values(entityPk).some(value => value !== undefined && value !== null && value !== "")
    : Boolean(entityPk);
  if (!hasPk) {
    return Promise.resolve(null);
  }
  const engineAppName = resolveContextEngineAppName(appName);
  let app;
  try {
    app = App.get(engineAppName);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`readOneEntityPk: app "${appName}" is not booted — cannot read `
      + `${entityType}.${fieldName}`, error);
    return Promise.resolve(null);
  }
  let stubRefetchSent = false;
  const refetchStubEntity = () => {
    if (stubRefetchSent) {
      return;
    }
    stubRefetchSent = true;
    const root = App.getRoot();
    const configurationPk = app.getSessionConfigurationPk?.() ?? undefined;
    const rootConfigurationPk = root.getSessionConfigurationPk?.() ?? undefined;
    root.apiPost(`/app/${root.getAppName()}/entities`, {
      ...rootConfigurationPk ? { configuration_pk: rootConfigurationPk } : {},
      requested_entities: [{
        type: entityType,
        pk: entityPk,
        appName: engineAppName,
        ...configurationPk ? { configurationPk } : {},
      }],
    }, response => {
      root.mergeResponsePayload(`/app/${root.getAppName()}/entities`, response);
    }, () => {});
  };
  const readOnce = () => {
    if (app.getEntityField(entityType, entityPk, "isHydrated") !== true) {
      return undefined;
    }
    const value = readUnsetRefAsNull(() => app.getEntityField(entityType, entityPk, fieldName))();
    if (value === null) {
      return null;
    }
    if (value === undefined) {
      return undefined;
    }
    const pk = value?.pk ?? value?.entityPk ?? value;
    if (pk && typeof pk === "object") {
      return { ...pk };
    }
    return typeof pk === "number" ? { id: pk } : undefined;
  };
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let attemptCount = 0;
    const attempt = () => {
      let resolved;
      try {
        resolved = readOnce();
      } catch (error) {
        reject(error);
        return;
      }
      if (resolved !== undefined) {
        resolve(resolved);
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        resolve(null);
        return;
      }
      attemptCount += 1;
      if (attemptCount === 2) {
        refetchStubEntity();
      }
      setTimeout(attempt, attemptCount <= 4 ? 50 : 250);
    };
    attempt();
  });
}

async function readConfigurationOneEntityPk(appName, configurationId, fieldName) {
  const pkMap = await readOneEntityPk(appName, "Configuration", { id: configurationId }, String(fieldName));
  return pkMap?.id ?? null;
}

function isAppBooted(appName) {
  try {
    App.get(appName);
    return true;
  } catch {
    return false;
  }
}

function waitForAppBoot(appName, timeoutMs = 20000) {
  return new Promise(resolve => {
    const startedAt = Date.now();
    const attempt = () => {
      if (isAppBooted(appName)) {
        resolve(true);
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        // eslint-disable-next-line no-console
        console.error(`waitForAppBoot: app "${appName}" never booted within ${timeoutMs}ms — its page contexts will not resolve`);
        resolve(false);
        return;
      }
      setTimeout(attempt, 100);
    };
    attempt();
  });
}
